import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {
	CheckboxCompat as Checkbox,
	DefaultButtonCompat as DefaultButton,
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	DialogType,
	PrimaryButtonCompat as PrimaryButton,
	SeparatorCompat as Separator,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import {useUserContext} from 'authentication/UserContext';
import {UserRole} from 'types';
import {
	isReaderRole,
	isUserFacingRole,
	READER_TOKEN_ROLE,
	roleDisplayName,
} from 'constants/roles';

interface RoleSwitcherDialogProps {
	onOpenChange?: (open: boolean) => void;
}


export const RoleSwitcherDialog: React.FC<RoleSwitcherDialogProps> = ({
	onOpenChange,
}) => {
	const {t} = useTranslation('components/usersettings', {
		keyPrefix: 'UserSettings',
	});
	const {eligibleRoles, activeRoles, setActiveRoles} = useUserContext();

	// Electable roles: user-facing business roles minus Reader (implicit) and system/app roles.
	const roleItems = React.useMemo(
		() =>
			(eligibleRoles ?? [])
				.filter(role => isUserFacingRole(role) && !isReaderRole(role))
				.map(role => ({role, text: roleDisplayName(role)}))
				.sort((a, b) => a.text.localeCompare(b.text)),
		[eligibleRoles],
	);
	const electableRoles = React.useMemo(
		() => roleItems.map(item => item.role),
		[roleItems],
	);
	const holdsReader = React.useMemo(
		() => (eligibleRoles ?? []).some(isReaderRole),
		[eligibleRoles],
	);

	const [isOpen, setIsOpen] = React.useState(false);
	const [selected, setSelected] = React.useState<UserRole[]>([]);

	// Nothing to switch: no elevated role, or one elevated role and no Reader baseline to toggle to.
	const canSwitch =
		electableRoles.length >= 2 || (electableRoles.length === 1 && holdsReader);
	if (!canSwitch) {
		return null;
	}

	const setOpen = (open: boolean) => {
		setIsOpen(open);
		onOpenChange?.(open);
	};

	const openDialog = () => {
		setSelected(
			activeRoles.length > 0
				? electableRoles.filter(role => activeRoles.includes(role))
				: [...electableRoles],
		);
		setOpen(true);
	};

	const allSelected = selected.length === electableRoles.length;

	const toggleAll = (checked: boolean) => {
		setSelected(checked ? [...electableRoles] : []);
	};

	const toggleRole = (role: UserRole, checked: boolean) => {
		setSelected(prev =>
			checked ? [...prev, role] : prev.filter(r => r !== role),
		);
	};

	const confirm = () => {
		if (selected.length === 0) {
			setActiveRoles([READER_TOKEN_ROLE] as unknown as UserRole[]);
		} else if (allSelected) {
			setActiveRoles([]);
		} else {
			setActiveRoles(selected);
		}
		setOpen(false);
	};

	return (
		<>
			<DefaultButton
				text={t('SwitchRole')}
				onClick={openDialog}
				styles={{
					root: {
						marginTop: 8,
						marginBottom: 12,
						marginLeft: 10,
						padding: '0 4px',
					},
				}}
			/>
			<Dialog
				hidden={!isOpen}
				onDismiss={() => setOpen(false)}
				styles={{main: {minWidth: '320px', maxWidth: '90vw', width: '380px'}}}
				dialogContentProps={{
					type: DialogType.normal,
					title: t('SwitchRole'),
					subText: t('SwitchRoleDescription'),
				}}
				modalProps={{isBlocking: true}}
			>
				<Stack tokens={{childrenGap: 10}}>
					<Checkbox
						label={t('AllRoles')}
						checked={allSelected}
						onChange={(_ev, checked) => toggleAll(!!checked)}
					/>
					<Separator styles={{root: {padding: 0}}} />
					<Stack tokens={{childrenGap: 8}}>
						{roleItems.map(({role, text}) => (
							<Checkbox
								key={role}
								label={text}
								checked={selected.includes(role)}
								onChange={(_ev, checked) => toggleRole(role, !!checked)}
							/>
						))}
					</Stack>
				</Stack>
				<DialogFooter>
					<PrimaryButton onClick={confirm} text={t('Confirm')} />
					<DefaultButton onClick={() => setOpen(false)} text={t('Cancel')} />
				</DialogFooter>
			</Dialog>
		</>
	);
};
